import { DOCUMENT } from '@angular/common';
import { Component, Input, OnInit, Output, EventEmitter, OnDestroy,  ChangeDetectorRef, Inject } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DateService } from '@app/services/date.service';
import moment from 'moment';
import { BehaviorSubject, from, Observable, of, Subject } from 'rxjs';
import { bufferCount, concatMap, delay, map, takeUntil, finalize } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

interface Month {
    id: string; // 2021-09-28
    value: string;
}

interface Calendar {
    w_0: Month[],
    w_1: Month[],
    w_2: Month[],
    w_3: Month[],
    w_4: Month[],
    w_5: Month[],
    header?: string;
    class?: string;
}

@Component({
    selector: 'clever-cleverdatepicker',
    templateUrl: './cleverdatepicker.component.html',
    styleUrls: ['./cleverdatepicker.component.scss'],
    standalone: false
})
export class CleverdatepickerComponent implements OnInit, OnDestroy {
    @Input() start: string;
    @Input() end: string;
    @Input() isOne: boolean = false;
    @Output() sendStart: EventEmitter<string> = new EventEmitter<string>();
    @Output() sendEnd: EventEmitter<string> = new EventEmitter<string>();
    @Output() sendRes: EventEmitter<any> = new EventEmitter<any>();
    header_week: string[] = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO'];
    currentFisrtDate: string = moment().startOf('month').format('YYYY-MM-DD');
    currentSecondDate: string = moment().startOf('month').format('YYYY-MM-DD');

    first_month: Calendar;
    second_month: Calendar;
    calendars: Observable<Calendar[]>;

    today: string = moment().format('YYYY-MM-DD');
    tmp_end: Month;
    first_header: string = '';
    second_header: string = '';
    media: string = '';

    showcalMobile: boolean = false;
    selectedState: string = '';
    holidays: string[] = [];
    germanStates = [
        { code: 'DE-BW', name: 'Baden-Württemberg' },
        { code: 'DE-BY', name: 'Bayern' },
        { code: 'DE-BE', name: 'Berlin' },
        { code: 'DE-BB', name: 'Brandenburg' },
        { code: 'DE-HB', name: 'Bremen' },
        { code: 'DE-HH', name: 'Hamburg' },
        { code: 'DE-HE', name: 'Hessen' },
        { code: 'DE-MV', name: 'Mecklenburg-Vorpommern' },
        { code: 'DE-NI', name: 'Niedersachsen' },
        { code: 'DE-NW', name: 'Nordrhein-Westfalen' },
        { code: 'DE-RP', name: 'Rheinland-Pfalz' },
        { code: 'DE-SL', name: 'Saarland' },
        { code: 'DE-SN', name: 'Sachsen' },
        { code: 'DE-ST', name: 'Sachsen-Anhalt' },
        { code: 'DE-SH', name: 'Schleswig-Holstein' },
        { code: 'DE-TH', name: 'Thüringen' }
    ];

    private unsubscribe$ = new Subject();
    initDate: BehaviorSubject<{ first: string, second: string }>;
    temInit: BehaviorSubject<Month>;
    constructor(
        protected mediaObserver: BreakpointObserver,
        protected dateService: DateService,
        protected cdr: ChangeDetectorRef,
        @Inject(DOCUMENT) protected document: any,
        private http: HttpClient
    ) {
        this.initDate = new BehaviorSubject(null);
        this.temInit = new BehaviorSubject(null);
    }

    ngOnInit(): void {
        if (!moment(this.start).isValid()) {
            this.start = null;
        } else {
            this.currentFisrtDate = moment(this.start).startOf('month').format('YYYY-MM-DD');
        }
        this.initMedia();
        this.initSub();
        this.initCals();
    }

    initMedia() {
        if (this.mediaObserver.isMatched(Breakpoints.XSmall)) {
            this.media = 'xs';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Small)) {
            this.media = 'sm';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Medium)) {
            this.media = 'md';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Large)) {
            this.media = 'lg';
        }
        if (this.mediaObserver.isMatched(Breakpoints.XLarge)) {
            this.media = 'xl';
        }
        this.cdr.detectChanges();
    }

    initSub() {
        this.initDate.asObservable().pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe((data: any) => {
            if (data) {
                this.initCal();
            }
        });
        this.initDate.next({ first: this.currentFisrtDate, second: this.currentSecondDate });

        this.temInit.asObservable().pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe((tmpdate) => {
            if (tmpdate?.id?.length > 0) {
                if (!this.end || this.end?.length === 0) {
                    this.tmp_end = tmpdate;
                } else {
                    this.tmp_end = null;
                }
            } else {
                this.tmp_end = null;
            }
        });
    }
    async onStateChange() {
        if (!this.selectedState) return;

        const currentYear = moment().year();
        const years = [currentYear, currentYear + 1]; // current + next year
        const holidays: string[] = [];
        const vacations: string[] = [];

        try {
            // --- 1️⃣ Fetch Public Holidays from Nager.Date ---
            const holidayResponses = await Promise.all(
                years.map(year =>
                    this.http.get<any[]>(`https://date.nager.at/api/v3/PublicHolidays/${year}/DE`).toPromise()
                )
            );

            const allHolidays = holidayResponses.flat();

            // Filter by Bundesland (if available in the counties field)
            const filteredHolidays = allHolidays.filter(
                h => !h.counties || h.counties.includes(this.selectedState)
            );

            holidays.push(...filteredHolidays.map(h => moment.utc(h.date).format('YYYY-MM-DD')));

            // --- 2️⃣ Fetch School Vacations from OpenHolidays API ---
            // Example: https://openholidaysapi.org/SchoolHolidays?countryIsoCode=DE&subdivisionCode=DE-BW&languageIsoCode=DE
            const vacationResponses = await Promise.all(
                years.map(year =>
                    this.http
                        .get<any[]>(`https://openholidaysapi.org/SchoolHolidays`, {
                            params: {
                                countryIsoCode: 'DE',
                                subdivisionCode: this.selectedState,
                                languageIsoCode: 'DE',
                                validFrom: `${year}-01-01`,
                                validTo: `${year}-12-31`
                            }
                        })
                        .toPromise()
                )
            );

            const allVacations = vacationResponses.flat();

            // Extract vacation date ranges
            allVacations.forEach(vac => {
                if (vac.startDate && vac.endDate) {
                    const start = moment(vac.startDate);
                    const end = moment(vac.endDate);
                    for (let d = start.clone(); d.isSameOrBefore(end); d.add(1, 'day')) {
                        vacations.push(d.format('YYYY-MM-DD'));
                    }
                }
            });

            // --- 3️⃣ Merge Holidays & Vacations ---
            this.holidays = Array.from(new Set([...holidays, ...vacations]));

            // --- 4️⃣ Highlight on Calendar ---
            this.highlightHolidays();

            console.log('✅ Combined holidays and vacations:', this.holidays.length);

        } catch (err) {
            console.error('❌ Error fetching holiday/vacation data:', err);
        }
    }




    highlightHolidays() {
        // --- Handle desktop view (two-month calendars)
        if (this.first_month && this.second_month) {
            [this.first_month, this.second_month].forEach(cal => {
                Object.keys(cal).forEach(week => {
                    if (Array.isArray(cal[week])) {
                        cal[week].forEach(day => {
                            if (day && day.id) {
                                day.holiday = this.holidays.includes(day.id);
                            }
                        });
                    }
                });
            });
        }

        // --- Handle mobile view (async calendars)
        if (this.calendars) {
            this.calendars = this.calendars.pipe(
                map((cals: any[]) => {
                    cals.forEach(cal => {
                        Object.keys(cal).forEach(week => {
                            if (Array.isArray(cal[week])) {
                                cal[week].forEach(day => {
                                    if (day && day.id) {
                                        day.holiday = this.holidays.includes(day.id);
                                    }
                                });
                            }
                        });
                    });
                    return cals;
                })
            );
        }

        this.cdr.detectChanges();
    }




    initCal() {
        this.first_month = this.dateService.initMonth(this.currentFisrtDate);
        this.currentSecondDate = moment(this.currentFisrtDate).startOf('month').add(1, 'months').format('YYYY-MM-DD');
        this.first_header = this.currentFisrtDate;
        this.second_month = this.dateService.initMonth(this.currentSecondDate);
        this.second_header = this.currentSecondDate;
        this.cdr.detectChanges();
    }

    initCals() {
        const count = 3;
        const items = [];
        this.dateService.getBigCalendar().pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe((arrr) => {
            const source$ = from(arrr);
            this.calendars = source$.pipe(
                bufferCount(count),
                concatMap((items, index) => of(items).pipe(delay(index === 0 ? 0 : 1))),
                map((arr: any) => {
                    items.push(...arr);
                    return [...items];
                }),
                finalize(() => {
                    setTimeout(() => {
                        this.gotopos();
                    }, 100);
                })
            );
            this.cdr.detectChanges();
        });
    }

    async gotopos() {
        const cl = 'cal_' + moment(this.start).format('YYYY-MM');
        const invalidElements = this.document.getElementById(cl);
        if (invalidElements) {
            invalidElements.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        this.cdr.detectChanges();
    }

    
    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    selectDay(md: Month) {
        if (md?.id?.length > 0) {
            if (this.isOne) {
                this.end = '';
                this.start = md.id;
            } else if (this.end?.length > 0 && this.start?.length > 0) {
                this.end = '';
                this.start = md.id;
            } else {
                if (this.start?.length > 0 && this.start < md.id) {
                    this.end = md.id;
                } else {
                    this.start = md.id;
                }
            }
        }
        this.sendStart.emit(this.start);
        this.sendEnd.emit(this.end);
        this.sendRes.emit({ start: this.start, end: this.end });
        this.cdr.detectChanges();
    }

    selectTmp_Day(md) {
        this.temInit.next(md);
    }

    previus() {
        this.currentFisrtDate = moment(this.currentFisrtDate).startOf('month').subtract(1, 'months').format('YYYY-MM-DD');
        this.initDate.next({ first: this.currentFisrtDate, second: this.currentSecondDate });
        this.highlightHolidays();
    }

    next() {
        this.currentFisrtDate = moment(this.currentFisrtDate).startOf('month').add(1, 'months').format('YYYY-MM-DD');
        this.initDate.next({ first: this.currentFisrtDate, second: this.currentSecondDate });
        this.highlightHolidays();
    }

    trackByFn(index, item) {
        return item?.class;
    }
}
